/**
 * Conversation context wrapper.
 * Canonical runtime scope is workspace + conversation.
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Conversation } from '@/types/conversations';
import type { Module } from '@/types/modules';
import { useConversations, useCreateConversation } from '@/features/conversations/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSession } from '@/features/auth/hooks/useSession';
import { queryKeys } from '@/lib/query/keys';
import {
  isConversationPinned,
  isWorkspaceDefaultConversation,
  parseConversationModules,
} from '@/types/conversations';
import { listPluginInstances, type PluginInstanceData } from '@/modules/_infrastructure/pluginApi';
import { buildConversationPath, resolveWorkspaceId } from '@/lib/workspace-routing';
import { pickNewChatName } from '@/lib/greetings';
import { isOnboardingComplete } from '@xavior/contracts';
import {
  fetchConversationSnapshot,
  syncConversationModules,
} from '@/features/conversations/lib/conversationAdapter';

// Storage keys for last viewed conversation per workspace.
const LAST_CONVERSATION_KEY_PREFIX = 'xavior_last_conversation:';

/**
 * Get the last viewed conversation ID for a workspace.
 */
export function getLastViewedConversation(containerId: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(`${LAST_CONVERSATION_KEY_PREFIX}${containerId}`);
  } catch {
    return null;
  }
}

/**
 * Save the last viewed conversation ID for a workspace.
 */
export function setLastViewedConversation(containerId: string, conversationId: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(`${LAST_CONVERSATION_KEY_PREFIX}${containerId}`, conversationId);
  } catch {
    // Ignore storage errors
  }
}

function buildPinnedFirstConversationIds(
  conversations: Conversation[],
  options?: { prioritizeUnpinnedConversationId?: string },
): string[] {
  const pinned: Conversation[] = [];
  const unpinned: Conversation[] = [];

  for (const conversation of conversations) {
    if (!conversation?.id) continue;
    if (isConversationPinned(conversation)) {
      pinned.push(conversation);
      continue;
    }
    unpinned.push(conversation);
  }

  const prioritizeId = options?.prioritizeUnpinnedConversationId;
  if (prioritizeId) {
    const targetIndex = unpinned.findIndex((conversation) => conversation.id === prioritizeId);
    if (targetIndex > 0) {
      const [targetConversation] = unpinned.splice(targetIndex, 1);
      unpinned.unshift(targetConversation);
    }
  }

  return [...pinned, ...unpinned].map((conversation) => conversation.id);
}

interface ConversationContextValue {
  // Current active conversation
  activeConversation: Conversation | null;
  setActiveConversation: (conversation: Conversation | null, options?: { skipFetch?: boolean }) => void;

  // All conversations from API
  allConversations: Conversation[];

  // Recent conversations (UI-only state)
  recentConversations: Conversation[];
  addToRecentConversations: (conversation: Conversation) => void;

  // Conversation mutations
  createConversation: (conversation: Conversation, options?: { preserveModuleId?: string | null }) => void;
  updateConversationModules: (
    conversationId: string,
    modulesOrPatcher: Module[] | ((current: Module[]) => Module[]),
  ) => Promise<void>;
  addModule: (conversationId: string, module: Module) => Promise<void>;
  deleteConversation: (conversationId: string) => Promise<void>;

  // Optimistic module patching (no server fetch, just local state)
  patchActiveModules: (conversationId: string, patcher: (modules: Module[]) => Module[]) => void;

  // Conversation renaming state
  renamingConversationId: string | null;
  setRenamingConversationId: (conversationId: string | null) => void;

  // Plugin instances for the active conversation
  pluginInstances: PluginInstanceData[];
  setPluginInstances: (instances: PluginInstanceData[]) => void;
  refreshPluginInstances: () => Promise<void>;
  // Sentinel: flips true after the first refreshPluginInstances() resolves
  // (success OR error) for the active conversation. Lets callers
  // (MainLayout URL guard) distinguish "still loading" from "loaded, empty"
  // so an empty plugin list for a conversation with zero plugins doesn't
  // deadlock a URL containing `plugin:` (#2721 follow-up).
  pluginInstancesLoaded: boolean;

  // Loading state
  isLoading: boolean;
  error: Error | null;

  // ID of the most recently created conversation (for instant greeting bypass)
  lastCreatedConversationId: string | null;
  clearLastCreatedConversationId: () => void;
}

const ConversationContext = createContext<ConversationContextValue | undefined>(undefined);

interface ConversationProviderProps {
  children: ReactNode;
}

export function ConversationProvider({ children }: ConversationProviderProps) {
  // UI-only state
  const [activeConversation, setActiveConversationState] = useState<Conversation | null>(null);
  const [recentConversations, setRecentConversations] = useState<Conversation[]>([]);
  const [renamingConversationId, setRenamingConversationId] = useState<string | null>(null);
  const [lastCreatedConversationId, setLastCreatedConversationId] = useState<string | null>(null);
  const [pluginInstances, setPluginInstances] = useState<PluginInstanceData[]>([]);
  // Tracks whether the most recent refreshPluginInstances() for the active
  // conversation has resolved. Reset to false on conversation switch, flipped
  // to true after fetch settles (success or error) — see refreshPluginInstances.
  const [pluginInstancesLoaded, setPluginInstancesLoaded] = useState<boolean>(false);

  // Track optimistic updates to prevent useEffect from reverting them
  const optimisticUpdateRef = useRef<number>(0);

  // Track the current conversation switch to prevent stale fetches from overwriting.
  const currentConversationSwitchRef = useRef<string | null>(null);

  // API hooks
  const { data: apiConversationsData, isLoading, error: apiError } = useConversations(undefined);
  const createConversationMutation = useCreateConversation();

  // Session for auth token and selected container
  const { token, selectedContainer, profile } = useSession();
  const workspaceId = resolveWorkspaceId(selectedContainer);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Guard: only attempt auto-create once per mount to prevent loops
  const autoCreateAttemptedRef = useRef(false);

  // On a workspace/identity switch, drop the active conversation immediately so a
  // conversation id from the PREVIOUS workspace is never sent against the new one.
  // Otherwise stale ids leak across the switch — 404s on /conversations and
  // chat_messages_session_id_fkey 500s — and the user gets bounced back to the
  // prior workspace (#3794). The data-load effect below then restores this
  // workspace's last-viewed/default conversation. A ref guards the initial mount
  // so we only clear on an actual change, not the first render.
  const prevWorkspaceIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      prevWorkspaceIdRef.current !== null &&
      prevWorkspaceIdRef.current !== workspaceId
    ) {
      setActiveConversationState(null);
      autoCreateAttemptedRef.current = false;
    }
    prevWorkspaceIdRef.current = workspaceId;
  }, [workspaceId]);

  // Set first API conversation as active when data loads, or clear if none exist.
  useEffect(() => {
    const conversations = apiConversationsData?.conversations;
    if (!conversations) return;

    const workspaceDefaultConversation = conversations.find((conversation) =>
      isWorkspaceDefaultConversation(conversation),
    );
    const fallbackConversation = workspaceDefaultConversation ?? conversations[0];

    // If no conversations exist, auto-create one.
    if (conversations.length === 0) {
      setActiveConversationState(null);
      // Wait for profile to load before deciding onboarding vs regular —
      // profile is null during fetch, and !null?.onboardingCompleted is true,
      // which would incorrectly create a 'Welcome' conversation for returning users.
      if (!profile) return;
      if (!autoCreateAttemptedRef.current) {
        autoCreateAttemptedRef.current = true;
        const name = !isOnboardingComplete(profile.onboardingState) ? 'Welcome' : pickNewChatName(new Set());
        createConversationMutation.mutate(
          { name },
          {
            onSuccess: (created) => {
              queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
              if (name !== 'Welcome') setLastCreatedConversationId(created.id);
              setActiveConversationState(created);
              navigate(buildConversationPath(workspaceId, created.id));
            },
            onError: (error) => {
              console.error('[ConversationContext] Auto-create conversation failed:', error);
              // Reset guard so the next data refresh retries the auto-create
              autoCreateAttemptedRef.current = false;
            },
          },
        );
      }
      return;
    }

    // Reset auto-create guard when conversations exist
    autoCreateAttemptedRef.current = false;

    setActiveConversationState(prev => {
      // If active conversation is not in the list (was deleted), set first conversation from API.
      if (prev && !conversations.find(conversation => conversation.id === prev.id)) {
        return fallbackConversation;
      }

      // If no active conversation, restore last viewed (or fall back to workspace default).
      if (!prev) {
        const containerId = selectedContainer?.id ?? selectedContainer?.ownerId;
        const lastViewedId = containerId ? getLastViewedConversation(containerId) : null;
        const lastViewed = lastViewedId
          ? conversations.find(c => c.id === lastViewedId)
          : null;
        const target = lastViewed ?? fallbackConversation;
        return {
          ...target,
          modules: target.modules ?? parseConversationModules(target.meta?.modules) ?? [],
        };
      }

      // Skip sync if an optimistic update happened recently (within 2 seconds)
      // This prevents stale API data from reverting optimistic module updates
      if (Date.now() - optimisticUpdateRef.current < 2000) {
        return prev;
      }

      // Sync with fresh data from API
      const freshConversation = conversations.find(conversation => conversation.id === prev.id);
      if (freshConversation) {
        const normalizedConversation = {
          ...freshConversation,
          modules: freshConversation.modules ?? parseConversationModules(freshConversation.meta?.modules) ?? []
        };
        // Update if any conversation property changed (name, icon, color, modules, etc.).
        const currentModules = JSON.stringify(prev.modules);
        const freshModules = JSON.stringify(normalizedConversation.modules);
        const hasModulesChanged = currentModules !== freshModules;
        const hasNameChanged = prev.name !== normalizedConversation.name;
        const hasIconChanged = prev.icon !== normalizedConversation.icon;
        const hasColorChanged = prev.color !== normalizedConversation.color;

        if (hasModulesChanged || hasNameChanged || hasIconChanged || hasColorChanged) {
          return normalizedConversation;
        }
      }
      return prev;
    });
  }, [apiConversationsData, profile]);

  // All conversations come from API.
  const allConversations = apiConversationsData?.conversations ?? [];
  const error = apiError as Error | null;

  // Load plugin instances when active conversation changes.
  const refreshPluginInstances = useCallback(async () => {
    if (!activeConversation?.id) {
      setPluginInstances([]);
      // No conversation = no plugins to fetch. Treat as "loaded, empty" so
      // any URL guard waiting on plugins doesn't deadlock.
      setPluginInstancesLoaded(true);
      return;
    }
    try {
      const instances = await listPluginInstances(activeConversation.id);
      setPluginInstances(instances);
    } catch (err) {
      console.error('[ConversationContext] Failed to load plugin instances:', err);
      setPluginInstances([]);
    } finally {
      // Flip the sentinel regardless of success/error. A legitimate empty
      // plugin list must look distinct from "still in-flight" so the URL
      // guard in MainLayout can fall through to default-module selection
      // instead of holding forever (#2721 follow-up).
      setPluginInstancesLoaded(true);
    }
  }, [activeConversation?.id]);

  useEffect(() => {
    // Reset the sentinel BEFORE kicking off the next fetch so a stale "true"
    // from the prior conversation can't pass the URL guard during the brief
    // overlap between conversation switch and fetch start.
    setPluginInstancesLoaded(false);
    refreshPluginInstances();
  }, [refreshPluginInstances]);

  const setActiveConversation = useCallback(async (conversation: Conversation | null, options?: { skipFetch?: boolean }) => {
    if (!conversation) {
      currentConversationSwitchRef.current = null;
      setActiveConversationState(null);
      return;
    }

    // Track this conversation switch so stale fetches for prior selections are ignored.
    const switchId = conversation.id;
    currentConversationSwitchRef.current = switchId;

    // Set initial state immediately with passed conversation data for responsiveness.
    const normalizedConversation = {
      ...conversation,
      modules: conversation.modules ?? parseConversationModules(conversation.meta?.modules) ?? [],
    };
    setActiveConversationState(normalizedConversation);

    // Save as last viewed conversation for this workspace (restore on next login).
    const selectedWorkspaceId = selectedContainer?.id ?? selectedContainer?.ownerId;
    if (selectedWorkspaceId) {
      setLastViewedConversation(selectedWorkspaceId, conversation.id);
    }

    // Scroll to top when switching conversations.
    window.scrollTo({ top: 0, behavior: 'auto' });

    // Update last accessed time and add to recent conversations.
    const updatedConversation = { ...conversation, last_accessed_at: new Date().toISOString() };
    setRecentConversations(prev => {
      const filtered = prev.filter(item => item.id !== conversation.id);
      return [updatedConversation, ...filtered].slice(0, 10);
    });

    if (!options?.skipFetch) {
      try {
        const freshData = await queryClient.fetchQuery({
          queryKey: queryKeys.conversations.detail(conversation.id),
          queryFn: () =>
            fetchConversationSnapshot(token ?? null, workspaceId, conversation.id, {
              includeArchivedModules: true,
            }),
          staleTime: 0,
        });

        if (currentConversationSwitchRef.current !== switchId) {
          return;
        }

        const freshConversation = (freshData as any)?.conversation;
        if (freshConversation) {
          const freshNormalized = {
            ...freshConversation,
            modules: freshConversation.modules ?? parseConversationModules(freshConversation.meta?.modules) ?? [],
          };
          setActiveConversationState(prev => prev?.id === conversation.id ? freshNormalized : prev);
        }
      } catch (err) {
        console.error('[ConversationContext] Failed to fetch fresh conversation data:', err);
      }
    }
  }, [token, queryClient, selectedContainer?.id, selectedContainer?.ownerId, workspaceId]);

  const patchActiveModules = useCallback((conversationId: string, patcher: (modules: Module[]) => Module[]) => {
    optimisticUpdateRef.current = Date.now();
    setActiveConversationState(prev => {
      if (!prev || prev.id !== conversationId) return prev;
      return { ...prev, modules: patcher(prev.modules || []) };
    });
  }, []);

  const addToRecentConversations = useCallback((conversation: Conversation) => {
    setRecentConversations(prev => {
      const filtered = prev.filter(item => item.id !== conversation.id);
      return [conversation, ...filtered].slice(0, 10);
    });
  }, []);

  const updateConversationModules = useCallback(async (
    conversationId: string,
    modulesOrPatcher: Module[] | ((current: Module[]) => Module[]),
  ) => {
    let payload;
    try {
      payload = await queryClient.fetchQuery({
        queryKey: queryKeys.conversations.detail(conversationId),
        queryFn: () =>
          fetchConversationSnapshot(token ?? null, workspaceId, conversationId, {
            includeArchivedModules: true,
          }),
      });
    } catch (error) {
      console.error('[ConversationContext] Failed to fetch conversation for module update:', error);
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.detail(conversationId) });
      return;
    }

    const conversation = payload?.conversation;
    if (!conversation) {
      console.error('[ConversationContext] Could not fetch conversation data for module update');
      return;
    }

    // When a patcher is supplied, apply it to the *freshly-fetched*
    // modules so two concurrent writes (e.g. add column then rename
    // column) compose instead of the second one clobbering the first
    // — each handler operates on the latest server-known state, not
    // a snapshot the component captured before the previous write
    // landed.
    //
    // Apply the patcher OUTSIDE the network try/catch so a patcher that
    // throws (e.g. target module deleted concurrently) propagates to the
    // caller, who can alert the user — rather than being swallowed
    // alongside transient network failures.
    const nextModules: Module[] = typeof modulesOrPatcher === 'function'
      ? modulesOrPatcher(conversation.modules || [])
      : [...modulesOrPatcher];

    // Mark optimistic update timestamp to prevent useEffect from reverting
    optimisticUpdateRef.current = Date.now();

    // Optimistically update UI state
    setActiveConversationState(prev =>
      prev && prev.id === conversationId ? { ...prev, modules: nextModules } : prev
    );

    try {
      await syncConversationModules(token ?? null, workspaceId, conversationId, nextModules);

      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.detail(conversationId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.lists(), refetchType: 'inactive' });
    } catch (error) {
      console.error('[ConversationContext] Failed to update conversation modules:', error);
      // Revert optimistic update on error
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.detail(conversationId) });
    }
  }, [token, queryClient, workspaceId]);

  const createConversation = useCallback((conversation: Conversation, options?: { preserveModuleId?: string | null }) => {
    const preserveModuleId = options?.preserveModuleId ?? null;
    const navOptions = preserveModuleId ? { moduleId: preserveModuleId } : { view: 'chat' as const };

    // Open the new chat instantly instead of waiting for the ~0.5s create
    // round-trip. Safe with the temp id (`conversation-*`): useSessionMessageLoader
    // skips the server GET and the greeting effect (useChat.ts) is gated on
    // `!startsWith('conversation-')`, so neither path 404s; `skipFetch` avoids
    // the snapshot fetch (refreshPluginInstances tolerates its own 404), and
    // `view:'chat'` keeps it chat-only so the URL guard's default-module
    // fallthrough can't open a pane. onSuccess swaps in the real id (which
    // fires the greeting via lastCreatedConversationId).
    setActiveConversation({ ...conversation, modules: [] }, { skipFetch: true });
    navigate(buildConversationPath(workspaceId, conversation.id, navOptions));

    createConversationMutation.mutate(
      {
        name: conversation.name,
        icon: conversation.icon,
      },
      {
        onSuccess: async (createdConversation) => {
          try {
            const reorderIds = buildPinnedFirstConversationIds(
              [
                ...allConversations.filter((existingConversation) => existingConversation.id !== createdConversation.id),
                createdConversation,
              ],
              { prioritizeUnpinnedConversationId: createdConversation.id },
            );

            if (reorderIds.length > 1) {
              api.workspaces.reorderConversations(token ?? null, workspaceId, reorderIds)
                .catch((err) => console.error('[ConversationContext] Failed to reorder:', err));
            }

            const modules: Module[] = [...(conversation.modules || [])];
            let activeConv: Conversation;

            if (modules.length > 0) {
              await syncConversationModules(token ?? null, workspaceId, createdConversation.id, modules);
              const payload = await fetchConversationSnapshot(token ?? null, workspaceId, createdConversation.id, {
                includeArchivedModules: true,
              });
              activeConv = payload.conversation;
            } else {
              activeConv = { ...createdConversation, modules: [] };
            }

            queryClient.setQueryData(queryKeys.conversations.detail(createdConversation.id), {
              conversation: activeConv,
            });
            queryClient.invalidateQueries({ queryKey: queryKeys.conversations.lists() });

            setLastCreatedConversationId(createdConversation.id);
            setActiveConversation(activeConv, { skipFetch: true });
            navigate(buildConversationPath(workspaceId, createdConversation.id, navOptions));
          } catch (error) {
            console.error('[ConversationContext] Failed to initialize conversation:', error);
            setLastCreatedConversationId(createdConversation.id);
            setActiveConversation(createdConversation);
            navigate(buildConversationPath(workspaceId, createdConversation.id, navOptions));
          }
        },
        onError: (error) => {
          console.error('[ConversationContext] Failed to create conversation:', error);
        },
      },
    );
  }, [allConversations, createConversationMutation, navigate, queryClient, setActiveConversation, token, workspaceId]);

  const addModule = useCallback(async (conversationId: string, module: Module) => {
    try {
      const payload = await queryClient.fetchQuery({
        queryKey: queryKeys.conversations.detail(conversationId),
        queryFn: () =>
          fetchConversationSnapshot(token ?? null, workspaceId, conversationId, {
            includeArchivedModules: true,
          }),
      });

      const conversation = payload?.conversation;
      if (!conversation) {
        console.error('[ConversationContext] Could not fetch conversation data for module addition');
        return;
      }

      const currentModules = conversation.modules ?? parseConversationModules(conversation.meta?.modules) ?? [];
      const updatedModules = [...currentModules, module];

      // Optimistically update UI state
      setActiveConversationState(prev =>
        prev && prev.id === conversationId ? { ...prev, modules: updatedModules } : prev
      );

      await syncConversationModules(token ?? null, workspaceId, conversationId, updatedModules);

      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.detail(conversationId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.lists(), refetchType: 'inactive' });

    } catch (error) {
      console.error('[ConversationContext] Failed to add module:', error);
      // Revert optimistic update on error
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.detail(conversationId) });
    }
  }, [token, queryClient, workspaceId]);

  const deleteConversation = useCallback(async (conversationId: string) => {
    try {
      await api.workspaces.deleteConversation(token ?? null, workspaceId, conversationId);

      // If deleting the active conversation, switch STRAIGHT to the next one
      // rather than clearing to null. Going through null blanks the chat to the
      // loading/empty screen for a beat (the "white Xavior loading screen") until
      // an effect re-selects — picking the next synchronously keeps the view
      // stable. Only when no chats remain do we clear (the auto-create effect
      // then makes a fresh one).
      if (activeConversation?.id === conversationId) {
        const remaining = (allConversations as Conversation[]).filter(
          (conversation) => conversation.id !== conversationId,
        );
        const nextConversation =
          remaining.find((conversation) => isWorkspaceDefaultConversation(conversation)) ?? remaining[0] ?? null;
        if (nextConversation) {
          void setActiveConversation(nextConversation);
          navigate(buildConversationPath(workspaceId, nextConversation.id));
        } else {
          void setActiveConversation(null);
        }
      }

      // Invalidate cache to refetch conversations list
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.lists(), refetchType: 'inactive' });
    } catch (error) {
      console.error('[ConversationContext] Failed to delete conversation:', error);
      throw error;
    }
  }, [token, queryClient, activeConversation, allConversations, navigate, setActiveConversation, workspaceId]);

  const clearLastCreatedConversationId = useCallback(() => {
    setLastCreatedConversationId(null);
  }, []);

  const value: ConversationContextValue = {
    activeConversation,
    setActiveConversation,
    recentConversations,
    addToRecentConversations,
    allConversations,
    createConversation,
    updateConversationModules,
    patchActiveModules,
    addModule,
    deleteConversation,
    renamingConversationId,
    setRenamingConversationId,
    pluginInstances,
    setPluginInstances,
    refreshPluginInstances,
    pluginInstancesLoaded,
    isLoading,
    error,
    lastCreatedConversationId,
    clearLastCreatedConversationId,
  };

  return <ConversationContext.Provider value={value}>{children}</ConversationContext.Provider>;
}

export function useConversation() {
  const context = useContext(ConversationContext);
  if (context === undefined) {
    throw new Error('useConversation must be used within a ConversationProvider');
  }
  return context;
}
